import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import {
  useNotifications,
  useUpdateAllNotifications,
  useUpdateNotification,
  type Notification,
} from "../api/notifications";
import { useAuth } from "../auth/auth-context";
import { initialsOf } from "../collaboration/PresenceAvatars";
import { colorForUser } from "../editor/collab";
import { cx } from "../lib/cx";
import { formatRelativeTime } from "../lib/time";
import { actorName, sentenceFor, subjectTitle, targetFor } from "../notifications/events";
import { BellIcon } from "../ui/icons";
import { Button } from "../ui/Button";

/**
 * The inbox (`docs/spec/notifications.md` §9): newest first, the badge driven by
 * the `unseen` count that comes back with every page, and the per-user
 * `notificationBadge` preference deciding whether that count is shown as a
 * number, as a dot, or not at all.
 *
 * Seen and archived are different things. Opening the popover marks what it
 * shows as viewed; dismissing an item archives it.
 */

function Avatar({ notification }: { notification: Notification }): ReactNode {
  const actor = notification.actor;
  const name = actorName(notification);
  if (actor?.avatarUrl != null && actor.avatarUrl !== "") {
    return <img src={actor.avatarUrl} alt="" className="h-7 w-7 rounded-full object-cover" />;
  }
  return (
    <span
      aria-hidden="true"
      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold text-white"
      style={{ backgroundColor: colorForUser(actor?.id ?? name) }}
    >
      {initialsOf(name)}
    </span>
  );
}

export function NotificationsBell(): ReactNode {
  const [open, setOpen] = useState(false);
  const [archived, setArchived] = useState(false);
  const navigate = useNavigate();
  const { session } = useAuth();
  const containerRef = useRef<HTMLDivElement | null>(null);

  const badge = session?.info.user.preferences?.notificationBadge ?? "count";
  const inbox = useNotifications(archived, session !== null);
  const update = useUpdateNotification();
  const updateAll = useUpdateAllNotifications();

  const unseen = inbox.data?.unseen ?? 0;
  const notifications = useMemo(() => inbox.data?.notifications ?? [], [inbox.data]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent): void => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  // §9: opening the inbox marks what it shows as viewed. Each row is marked at
  // most once per session, so the refetch every mark triggers cannot loop.
  const markedRef = useRef(new Set<string>());
  const markViewed = update.mutate;
  useEffect(() => {
    if (!open || archived) return;
    for (const notification of notifications) {
      if (notification.viewedAt !== null || markedRef.current.has(notification.id)) continue;
      markedRef.current.add(notification.id);
      markViewed({ id: notification.id, viewedAt: new Date().toISOString() });
    }
  }, [archived, markViewed, notifications, open]);

  const openItem = (notification: Notification): void => {
    const target = targetFor(notification);
    if (notification.viewedAt === null) {
      update.mutate({ id: notification.id, viewedAt: new Date().toISOString() });
    }
    if (target === null) return;
    setOpen(false);
    void navigate(target);
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        aria-label={
          badge === "count" && unseen > 0
            ? `Notifications (${String(unseen)} unread)`
            : "Notifications"
        }
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => {
          setOpen((value) => !value);
        }}
        className="relative rounded-md p-1.5 text-muted hover:bg-hover"
      >
        <BellIcon />
        {unseen > 0 && badge === "count" && (
          <span className="absolute -top-0.5 -right-0.5 min-w-4 rounded-full bg-accent px-1 text-[10px] leading-4 font-semibold text-on-accent">
            {unseen > 99 ? "99+" : unseen}
          </span>
        )}
        {unseen > 0 && badge === "indicator" && (
          <span
            data-testid="notification-indicator"
            className="absolute top-0.5 right-0.5 h-2 w-2 rounded-full bg-accent"
          />
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Notifications"
          className="app-shadow absolute right-0 z-20 mt-2 w-96 rounded-lg border border-line bg-raised p-2"
        >
          <header className="flex items-center gap-2 px-1 pb-2">
            <h2 className="text-sm font-semibold text-ink">Notifications</h2>
            <div className="ml-auto flex items-center gap-1">
              <Button
                size="sm"
                aria-pressed={archived}
                onClick={() => {
                  setArchived((value) => !value);
                }}
              >
                {archived ? "Inbox" : "Archived"}
              </Button>
              <Button
                size="sm"
                disabled={updateAll.isPending}
                onClick={() => {
                  updateAll.mutate({ viewedAt: new Date().toISOString() });
                }}
              >
                Mark all read
              </Button>
            </div>
          </header>

          {inbox.isPending && (
            <p role="status" className="px-1 py-2 text-sm text-muted">
              Loading notifications…
            </p>
          )}
          {inbox.isError && (
            <p role="alert" className="px-1 py-2 text-sm text-danger">
              {inbox.error.message}
            </p>
          )}
          {inbox.data !== undefined && notifications.length === 0 && (
            <p className="px-1 py-2 text-sm text-muted">
              {archived ? "Nothing archived." : "You have no notifications."}
            </p>
          )}

          <ul aria-label="Notifications" className="max-h-96 overflow-y-auto">
            {notifications.map((notification) => {
              const title = subjectTitle(notification);
              const clickable = targetFor(notification) !== null;
              return (
                <li
                  key={notification.id}
                  className={cx(
                    "flex items-start gap-2 rounded-md px-1 py-2",
                    notification.viewedAt === null && "bg-hover",
                  )}
                >
                  <Avatar notification={notification} />
                  <div className="min-w-0 flex-1">
                    <button
                      type="button"
                      disabled={!clickable}
                      onClick={() => {
                        openItem(notification);
                      }}
                      className="block w-full text-left text-sm text-ink disabled:cursor-default"
                    >
                      <span className="font-medium">{actorName(notification)}</span>{" "}
                      {sentenceFor(notification)}{" "}
                      {title !== null && <span className="font-medium">{title}</span>}
                    </button>
                    <time className="text-xs text-muted" dateTime={notification.createdAt}>
                      {formatRelativeTime(notification.createdAt)}
                    </time>
                  </div>
                  {notification.archivedAt === null && (
                    <button
                      type="button"
                      aria-label="Archive notification"
                      className="rounded px-1 text-xs text-muted hover:bg-hover"
                      onClick={() => {
                        update.mutate({
                          id: notification.id,
                          archivedAt: new Date().toISOString(),
                        });
                      }}
                    >
                      ×
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
